/**
 * blockMutations.js — user-defined reusable blocks
 *
 * A "block" is a named group of edges, circles, arcs, and polylines
 * captured from the current selection. Inserting a block places a
 * reference (transform: position + rotation + scale) — editing the
 * definition updates all instances.
 *
 * State shape:
 *   st.blockDefs:  { [id]: { id, name, entities: {edges,circles,arcs,polylines}, bbox } }
 *   st.blockRefs:  { [id]: { id, defId, x, y, rotation, scale } }
 */

import { mkId } from "../utils/coords";
import { Geo } from "../utils/geo";
import { mutAddCircle } from "./primitives";

// ─── Create block from selected entities ──────────────────────────────────────

/**
 * mutDefineBlock(name, selEdgeIds, selPrimIds, st)
 *
 * Captures the selected geometry into a named block definition.
 * Origin is set to the bounding box centroid of the selection.
 */
export function mutDefineBlock(name, selEdgeIds = [], selPrimIds = {}, st) {
  if (!selEdgeIds.length && !Object.keys(selPrimIds).length) return st;

  const id = mkId("blk");

  // Collect all node positions to compute centroid
  const pts = [];
  const edges = {}, circles = {}, arcs = {}, polylines = {};

  for (const eId of selEdgeIds) {
    const e = st.edges[eId]; if (!e) continue;
    const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
    if (!nA || !nB) continue;
    edges[eId] = { ...e };
    pts.push(nA, nB);
  }

  for (const cId of (selPrimIds.circles || [])) {
    const c = (st.circles || {})[cId]; if (!c) continue;
    circles[cId] = { ...c };
    pts.push({ x: c.cx, y: c.cy });
  }
  for (const aId of (selPrimIds.arcs || [])) {
    const a = (st.arcs || {})[aId]; if (!a) continue;
    arcs[aId] = { ...a };
    pts.push({ x: a.cx, y: a.cy });
  }
  for (const pId of (selPrimIds.polylines || [])) {
    const p = (st.polylines || {})[pId]; if (!p) continue;
    polylines[pId] = { ...p };
    if (p.pts) pts.push(...p.pts);
  }

  if (!pts.length) return st;

  // Compute centroid (becomes block origin)
  const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p.y, 0) / pts.length;
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
  const bbox = { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };

  const blockDef = {
    id, name: name || `Block ${id.slice(0,6)}`,
    entities: { edges, circles, arcs, polylines },
    origin: { x: Math.round(cx), y: Math.round(cy) },
    bbox,
    createdAt: Date.now(),
  };

  return {
    ...st,
    blockDefs: { ...(st.blockDefs || {}), [id]: blockDef },
  };
}

// ─── Insert block reference ───────────────────────────────────────────────────

/**
 * mutInsertBlock(defId, x, y, rotation, scale, st)
 *
 * Places a reference to a block definition at the given position.
 * The reference stores the transform; the geometry is read from the def.
 */
export function mutInsertBlock(defId, x, y, rotation = 0, scale = 1, st) {
  const def = (st.blockDefs || {})[defId]; if (!def) return st;
  const id  = mkId("bref");
  const ref = { id, defId, x, y, rotation, scale };
  return {
    ...st,
    blockRefs: { ...(st.blockRefs || {}), [id]: ref },
  };
}

export function mutMoveBlockRef(refId, x, y, st) {
  const ref = (st.blockRefs || {})[refId]; if (!ref) return st;
  return { ...st, blockRefs: { ...st.blockRefs, [refId]: { ...ref, x, y } } };
}

export function mutRotateBlockRef(refId, rotation, st) {
  const ref = (st.blockRefs || {})[refId]; if (!ref) return st;
  return { ...st, blockRefs: { ...st.blockRefs, [refId]: { ...ref, rotation } } };
}

export function mutDeleteBlockRef(refId, st) {
  const refs = { ...(st.blockRefs || {}) }; delete refs[refId];
  return { ...st, blockRefs: refs };
}

export function mutDeleteBlockDef(defId, st) {
  const defs = { ...(st.blockDefs || {}) }; delete defs[defId];
  // Also remove all references to this def
  const refs = Object.fromEntries(
    Object.entries(st.blockRefs || {}).filter(([, r]) => r.defId !== defId)
  );
  return { ...st, blockDefs: defs, blockRefs: refs };
}

export function mutRenameBlock(defId, name, st) {
  const def = (st.blockDefs || {})[defId]; if (!def) return st;
  return { ...st, blockDefs: { ...st.blockDefs, [defId]: { ...def, name } } };
}

// ─── Explode block reference ──────────────────────────────────────────────────

/**
 * mutExplodeBlock(refId, st)
 *
 * Converts a block reference into real geometry at the ref's transform.
 * The reference is removed; entities are added to the drawing directly.
 */
export function mutExplodeBlock(refId, st) {
  const ref = (st.blockRefs || {})[refId]; if (!ref) return st;
  const def = (st.blockDefs || {})[ref.defId]; if (!def) return st;

  const cos = Math.cos(ref.rotation * Math.PI / 180);
  const sin = Math.sin(ref.rotation * Math.PI / 180);

  function transformPt(p, originX, originY) {
    const dx = (p.x - originX) * ref.scale;
    const dy = (p.y - originY) * ref.scale;
    return {
      x: Math.round(ref.x + dx * cos - dy * sin),
      y: Math.round(ref.y + dx * sin + dy * cos),
    };
  }

  let s = st;

  // Add transformed edges
  for (const e of Object.values(def.entities.edges || {})) {
    const ab = st.halfEdges?.[e.halfEdgeA], ba = st.halfEdges?.[e.halfEdgeB];
    if (!ab || !ba) continue;
    const nA = st.nodes?.[ab.originId], nB = st.nodes?.[ba.originId];
    if (!nA || !nB) continue;
    const tA = transformPt(nA, def.origin.x, def.origin.y);
    const tB = transformPt(nB, def.origin.x, def.origin.y);
    const nAId = mkId("n"), nBId = mkId("n");
    const heAB = mkId("he"), heBA = mkId("he"), eId = mkId("e");
    s = {
      ...s,
      nodes: {
        ...s.nodes,
        [nAId]: { id: nAId, x: tA.x, y: tA.y, halfEdgeIds: [heAB] },
        [nBId]: { id: nBId, x: tB.x, y: tB.y, halfEdgeIds: [heBA] },
      },
      halfEdges: {
        ...s.halfEdges,
        [heAB]: { id: heAB, originId: nAId, twinId: heBA, nextId: null, faceId: null },
        [heBA]: { id: heBA, originId: nBId, twinId: heAB, nextId: null, faceId: null },
      },
      edges: {
        ...s.edges,
        [eId]: { ...e, id: eId, halfEdgeA: heAB, halfEdgeB: heBA, constraintIds: [] },
      },
    };
  }

  // Add transformed circles
  for (const c of Object.values(def.entities.circles || {})) {
    const tp = transformPt({ x: c.cx, y: c.cy }, def.origin.x, def.origin.y);
    s = mutAddCircle(tp.x, tp.y, c.r * ref.scale, c.layerId, s);
  }

  // Remove the reference
  const refs = { ...s.blockRefs }; delete refs[refId];
  return { ...s, blockRefs: refs };
}
